/**
 * Which building a showing actually happened in, and which hall — decided
 * with no imports so it can be tested from the Worker's suite (CLAUDE.md: a
 * decision belongs in a dependency-free file, the same reason `rowMeta.ts`
 * and `runPattern.ts` are).
 *
 * A showing's own `venue_id` can name a hall under a building rather than the
 * building itself — the Philharmonie's Großer Saal is its own `venues` row
 * with a `parent_id` — and an entry's own `venue_id`/`building_id` can be
 * filed at whichever cinema a weekly parse saw first, which says nothing
 * about which of a building's halls a given showing is actually in. WP 3.4a's
 * `Places` detail page used to resolve this inline, once per building and
 * untested; this is the same resolution, pulled out so it can be checked
 * against a fixture instead of a screenshot.
 */

export interface HallVenue {
  id: string
  name: string
  parent_id: string | null
}

export interface PlacedShowing {
  entry_id: string | null
  /** Optional as well as nullable so `ShowingLite` (`components/CardBody.tsx`)
   *  satisfies this structurally without a cast — it declares both as `?`. */
  venue_id?: string | null
  building_id?: string | null
}

/** The building a venue row belongs to — itself, where it is not a hall
 *  under something else. */
export function buildingOf(venueId: string, venues: Map<string, HallVenue>): string {
  return venues.get(venueId)?.parent_id ?? venueId
}

/**
 * Every entry with a showing at `buildingId`, and the hall each one plays
 * there — `null` when it plays the building itself rather than a named hall
 * under it.
 *
 * An entry can have more than one showing at the same building — a run with
 * a performance in the Großer Saal and one in the Kammermusiksaal — and once
 * a hall name has been found for it, a later showing with no hall on it must
 * not erase it: `null` never overwrites a real answer.
 */
export function hallOfEachEntry(
  buildingId: string,
  showings: PlacedShowing[],
  venues: Map<string, HallVenue>,
): Map<string, string | null> {
  const out = new Map<string, string | null>()
  for (const s of showings) {
    if (!s.entry_id || !s.venue_id) continue
    const b = s.building_id ?? buildingOf(s.venue_id, venues)
    if (b !== buildingId) continue
    const hall = s.venue_id === buildingId ? null : (venues.get(s.venue_id)?.name ?? null)
    const existing = out.get(s.entry_id)
    if (existing === undefined || (existing == null && hall)) out.set(s.entry_id, hall)
  }
  return out
}

export interface PlacedRow {
  id: string
  venue_id: string | null
  building_id?: string | null
  venue?: string | null
}

/**
 * Every row that belongs on `buildingId`'s programme, with the hall (if any)
 * each one plays there.
 *
 * A showing beats the row's own filing: an entry with any showing at all only
 * appears where a showing actually places it — a film's `venue_id` is
 * whichever cinema a weekly parse saw first, which is not a fact about where
 * else it plays. Only a row with no showing at all (an exhibition run, or a
 * showing with no venue on it) falls back to its own venue, the same rule
 * `Places`' original `placesOf` used for the index.
 */
export function programmeFor<T extends PlacedRow>(
  buildingId: string,
  rows: T[],
  showings: PlacedShowing[],
  venues: Map<string, HallVenue>,
): { row: T; hall: string | null }[] {
  const hallOf = hallOfEachEntry(buildingId, showings, venues)
  const hasShowing = new Set(showings.filter(s => s.entry_id).map(s => s.entry_id!))

  const out: { row: T; hall: string | null }[] = []
  for (const row of rows) {
    if (hallOf.has(row.id)) { out.push({ row, hall: hallOf.get(row.id)! }); continue }
    if (hasShowing.has(row.id)) continue
    if (!row.venue_id) continue
    const b = row.building_id ?? buildingOf(row.venue_id, venues)
    if (b !== buildingId) continue
    out.push({ row, hall: row.venue_id === buildingId ? null : (row.venue ?? null) })
  }
  return out
}
